import './AdvancedSearchStyle.scss';

import {
  DatePickerV2,
  Dropdown,
} from '@tfecom-thermofisher/react-komodo-design';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { dateOptions, statusOptions } from 'src/constants/constant';
import { Icon } from 'src/core/components';

const AdvancedSearchComponent = ({
  radioSelected,
  filterFormData,
  handleFilterFormEvent,
  handleAdvancedSearch,
  validationErrors = {
    startDate: '',
    endDate: '',
    general: '',
    range: '',
    status: '',
  },
}) => {
  const [dateErrors, setDateErrors] = useState({
    startDate: '',
    endDate: '',
    general: '',
  });

  // Merge parent validation errors with local errors
  const displayErrors = {
    startDate: validationErrors.startDate || dateErrors.startDate,
    endDate: validationErrors.endDate || dateErrors.endDate,
    general: validationErrors.general || dateErrors.general,
    range: validationErrors.range || dateErrors.range,
    status: validationErrors.status || dateErrors.status,
  };

  const handleStatusSelect = (selected) =>
    handleFilterFormEvent('STATUS_SELECT', selected, displayErrors);

  const handleDateSelect = (selected) =>
    handleFilterFormEvent('DATE_RANGE_SELECT', selected);

  const validateDateRange = (startDate, endDate) => {
    const errors = {
      startDate: '',
      endDate: '',
      general: '',
    };

    // Parse dates for comparison
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Check for invalid dates
    if (isNaN(start.getTime())) {
      errors.startDate = 'Please enter a valid start date.';
      return errors;
    }

    if (isNaN(end.getTime())) {
      errors.endDate = 'Please enter a valid end date.';
      return errors;
    }

    // Check if start date is before end date
    if (start > end) {
      errors.endDate = 'End date cannot be earlier than start date.';
      return errors;
    }

    // Check if date range exceeds 1 year
    const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
    if (end.getTime() - start.getTime() > oneYearInMs) {
      errors.general = 'Date range cannot exceed 1 year.';
      return errors;
    }

    return errors;
  };

  const handleDateChange = (date) => {
    // Always update the form state first to reflect the UI change
    handleFilterFormEvent('DATE_CHANGE', date);

    const newStartDate =
      date.startDate !== undefined ? date.startDate : filterFormData.startDate;
    const newEndDate =
      date.endDate !== undefined ? date.endDate : filterFormData.endDate;

    // Clear previous errors when a new date is being selected
    setDateErrors({
      startDate: '',
      endDate: '',
      general: '',
    });

    // Only validate when both dates are present
    if (newStartDate && newEndDate) {
      const errors = validateDateRange(newStartDate, newEndDate);
      setDateErrors(errors);
    }
  };

  // Clear errors when switching away from date mode
  useEffect(() => {
    if (radioSelected === 'range') {
      setDateErrors({
        startDate: '',
        endDate: '',
        general: '',
      });
    }
  }, [radioSelected]);

  return (
    <div className="advanced-search-container p-2 py-md-4 px-md-3">
      <span className="close-icon" onClick={handleAdvancedSearch}>
        <Icon size="16" name="close-mono" />
      </span>
      <div className="container-xxl">
        <div className="row">
          <div className="col-12 col-lg-4 divider">
            <div className="date-picker-container">
              <div className="row">
                <div className="col-12">
                  <DatePickerV2
                    onSelect={(date) =>
                      handleDateChange({ startDate: date, type: 'date' })
                    }
                    startDate={filterFormData.startDate}
                    startLabel="Start Date"
                    placeholder="Select start date..."
                    label="Start Date Picker"
                    className="start-date-picker"
                  />
                  {displayErrors.startDate && (
                    <div className="d-flex info-warning-text">
                      <Icon size="16" name="info-mono" />
                      <span className="ms-1">{displayErrors.startDate}</span>
                    </div>
                  )}
                </div>
                <div className="col-12 mt-3">
                  <DatePickerV2
                    onSelect={(date) => {
                      handleDateChange({ endDate: date, type: 'date' });
                    }}
                    startDate={filterFormData.endDate}
                    startLabel="End Date"
                    placeholder="Select end date..."
                    label="End Date Picker"
                    className="end-date-picker"
                  />
                  {displayErrors.endDate && (
                    <div className="d-flex info-warning-text">
                      <Icon size="16" name="info-mono" />
                      <span className="ms-1">{displayErrors.endDate}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4 divider">
            <div className="quick-range-dropdown">
              <Dropdown
                label="Quick Range"
                options={dateOptions}
                onSelect={(data) =>
                  handleDateSelect({ ...data, type: 'range' })
                }
                selectedOptions={dateOptions.find(
                  (date) => date.value === filterFormData.dateRange
                )}
              />
            </div>

            {/* {displayErrors.range && (
              <div className="d-flex info-warning-text">
                <Icon size="16" name="info-mono" />
                <span className="ms-2">{displayErrors.range}</span>
              </div>
            )} */}
          </div>
          <div className="col-12 col-lg-4">
            <div className="status-dropdown">
              <Dropdown
                label="Status"
                options={statusOptions}
                onSelect={(data) =>
                  handleStatusSelect({ ...data, type: 'status' })
                }
                selectedOptions={statusOptions.find(
                  (status) => status.value === filterFormData.status
                )}
              />
            </div>

            {/* {displayErrors.status && (
              <div className="d-flex info-warning-text">
                <Icon size="16" name="info-mono" />
                <span className="ms-2">{displayErrors.status}</span>
              </div>
            )} */}
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            {displayErrors.general && (
              <div className="d-flex info-warning-text">
                <Icon size="16" name="info-mono" />
                <span className="ms-2">{displayErrors.general}</span>
              </div>
            )}

            <div className="d-flex info-text">
              <Icon size="16" name="info-mono" />
              <span>
                Use the search bar above to input criteria and begin your
                advanced search
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AdvancedSearchComponent.propTypes = {
  radioSelected: PropTypes.string.isRequired,
  filterFormData: PropTypes.object.isRequired,
  handleFilterFormEvent: PropTypes.func.isRequired,
  handleAdvancedSearch: PropTypes.func.isRequired,
  validationErrors: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    general: PropTypes.string,
  }),
};

export default AdvancedSearchComponent;
